import {NoteGroupIcon} from "crqlar-react-common/dist/components/note/NoteGroupIcon";
import {useNoteGroupIndex} from "crqlar-react-common/dist/utils/hooks/notes/useNoteGroupIndex";
import React from "react";
import DisabledByDefaultIcon from '@mui/icons-material/DisabledByDefault';
import {differenceInMinutes} from "date-fns";
import {Box, darken, Divider, Stack, Typography, useTheme} from "@mui/material";
import {alpha} from "@mui/material/styles";
import HotelIcon from '@mui/icons-material/Hotel';
import {MbscCalendarEventData} from "@mobiscroll/react";
import {Reservation, ReservationStatus} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {CommonThemeColors} from "crqlar-react-common/dist/components/theme";
import {getCurrentStay, Profile} from "crqlar-react-common/dist/models/profile/profile.model";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BookOnlineIcon from '@mui/icons-material/BookOnline';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import WarningIcon from '@mui/icons-material/Warning';
import LogoutIcon from '@mui/icons-material/Logout';
import CachedIcon from '@mui/icons-material/Cached';
import {formatProfileListName} from "crqlar-react-common/dist/utils/profile.util";
import {hasRequiredResources} from "crqlar-react-common/dist/utils/hooks/reservations/reservations.utils";
import {AgeCategory} from "crqlar-react-common/dist/models/ageCategory/ageCategory.model";
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import {useResources} from "crqlar-react-common/dist/utils/hooks/resources/useResources";
import {useSector} from "crqlar-react-common/dist/utils/hooks/sector/useSectors";
import CheckIcon from '@mui/icons-material/Check';
import {useService} from "crqlar-react-common/dist/utils/hooks/services/useServices";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import {useAllSectors} from "crqlar-react-common/dist/utils/hooks/sectors/useSectors";
import {SectorType} from "crqlar-react-common/dist/models/sector/sector.model";
import {getEventColorBasedOnStatus} from "crqlar-react-common/dist/utils/color.util";
import {getReservationDisplayGuestCounts} from "crqlar-react-common/dist/utils/reservationGuestCounts.util";
import {resolveAdultChildGuestCounts} from "../common/ageCategoryDefaults";

type CalendarView = 'timeGrid' | 'timeline'
const NARROW_EVENT_CONTAINER_QUERY = '@container (max-width: 96px)'
const TIMELINE_NOTE_CONTAINER_QUERY = '@container (min-width: 220px) and (min-height: 72px)'

interface Props {
    reservation?: Reservation,
    profiles?: Profile[],
    onReservationClick?: () => void,
    event?: MbscCalendarEventData
    // Marks the calendar event located from the reservation search.
    highlightSearchResult?: boolean
    ageCategories?: AgeCategory[]
    mbscResourceColumnType?: 'resources' | 'services',
    // Calendar events use view-specific dense layouts. Other callers keep the summary layout.
    calendarView?: CalendarView
}

const ReservationEventPreview = ({
                                     reservation,
                                     profiles,
                                     onReservationClick,
                                     event,
                                     highlightSearchResult,
                                     ageCategories,
                                     mbscResourceColumnType,
                                     calendarView
                                 }: Props) => {

    const {t, localize} = useLocalize()
    const theme = useTheme()
    const noteGroupIndex = useNoteGroupIndex()
    const isCalendarEvent = !!calendarView
    // Same pick as the calendar tooltip: profiles arrive sorted by name, so [0] can be a nameless companion
    const primaryProfile = profiles?.find(p => p.id === reservation?.primaryProfileId) ?? profiles?.[0]
    const resName = formatProfileListName(primaryProfile) || event?.title || '-'

    const restaurantSectorId = useAllSectors().find(s => s.sectorType === SectorType.RESTAURANT)?.id
    const sector = useSector(reservation?.sectorId)
    const resources = useResources(reservation?.resourceIds || [])
    const service = useService(reservation?.serviceId)

    // In restaurant reservations, only child resources represent the attached tables.
    const numOfAttachedResources = sector?.sectorType === SectorType.RESTAURANT
        ? resources.filter(r => r.parentId).length
        : resources.length

    const reservationStart = reservation?.expectedStartDay
    const reservationEnd = reservation?.expectedEndDay
    const currentStay = getCurrentStay(primaryProfile)
    const allStays = [
        ...(primaryProfile?.activeStays ?? []),
        ...(primaryProfile?.pastStays ?? []),
        ...(primaryProfile?.upcomingStays ?? [])
    ]

    // Deprecated code -> now showing only the current stay and not matching stay to avoid confusion and keep the room label consistently accross the app
    // const matchingStay = allStays.find(stay =>
    //     stay.startDay &&
    //     stay.endDay &&
    //     reservationStart &&
    //     reservationEnd &&
    //     stay.startDay <= reservationStart &&
    //     stay.endDay >= reservationEnd
    // ) ?? currentStay

    const room = currentStay?.roomId ?? '-'
    const serviceName = localize(service?.nameLocalized) || service?.name || ''
    const contextText = [reservation?.fromBookingEngine ? t('Online') : room, '|' + ' ' + serviceName]
        .filter(Boolean)
        .join(' ')
    const noteEntry = reservation?.notes
        ?.find(item => !item.hidden && item.note?.trim())
    const note = noteEntry?.note?.trim()
    const isSerial = Boolean(reservation?.reservationGroupId)

    const duration = reservation?.expectedEndDate && reservation?.expectedStartDate
        ? differenceInMinutes(reservation.expectedEndDate, reservation.expectedStartDate)
        : undefined
    let fontSizeTop = isCalendarEvent
        ? theme.typography.body2.fontSize as string
        : theme.typography.h6.fontSize as string
    let fontSizeBottom = isCalendarEvent
        ? theme.typography.body2.fontSize as string
        : theme.typography.body1.fontSize as string

    if (!isCalendarEvent && duration && duration <= 15) {
        fontSizeTop = theme.typography.body1.fontSize as string
        fontSizeBottom = theme.typography.caption.fontSize as string
    }

    const {
        adultCount,
        childCount,
        totalCount: totalGuestCount,
        hasCompleteBreakdown: hasCompleteAgeBreakdown,
    } = resolveAdultChildGuestCounts(ageCategories ?? [], getReservationDisplayGuestCounts(reservation))
    const guestBreakdownLabel = ` (${adultCount}/${childCount})`
    const guestCountLabel = hasCompleteAgeBreakdown
        ? `${totalGuestCount}${guestBreakdownLabel}`
        : `${totalGuestCount}`
    const guestTitle = `${resName} · ${guestCountLabel}`

    let textColor = service?.color?.text || CommonThemeColors.black
    let backgroundColor = service?.color?.background || CommonThemeColors.sys.success.background

    // Unassigned reservations use the warning surface independently of their status.
    if (mbscResourceColumnType === 'services') {
        if (!reservation?.serviceId) {
            backgroundColor = CommonThemeColors.sys.warning.background
            textColor = CommonThemeColors.black
        }
    } else if (
        !reservation?.resourceIds?.length ||
        !hasRequiredResources(service, resources, restaurantSectorId)
    ) {
        backgroundColor = CommonThemeColors.sys.warning.background
        textColor = CommonThemeColors.black
    }

    const statusLabel = reservation?.status
        ? t(`Reservation status ${reservation.status}`)
        : t('Reservation')
    const statusAccentColor = getEventColorBasedOnStatus(reservation?.status)
        || CommonThemeColors.sys.palette.blueGray["600"]
    const eventIconSx = {
        color: textColor,
        fontSize: isCalendarEvent ? {xs: '14px', md: '16px'} : '20px'
    }
    const secondaryIconSx = {
        color: textColor,
        fontSize: isCalendarEvent ? {xs: '14px', md: '16px'} : 18
    }
    const calendarTextSx = {
        fontSize: {
            xs: theme.typography.body2.fontSize,
            md: theme.typography.body1.fontSize,
        },
        lineHeight: '18px'
    }
    const calendarRowSx = {
        display: 'grid',
        gridTemplateColumns: '16px minmax(0, 1fr) auto',
        columnGap: '4px',
        alignItems: 'center',
        width: '100%',
        minWidth: 0,
        flexShrink: 0
    }
    const calendarIconRailSx = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    }
    const calendarTopRowSx = {
        ...calendarRowSx,
        [NARROW_EVENT_CONTAINER_QUERY]: {
            columnGap: 0
        }
    }

    let statusIcon: React.ReactNode
    switch (reservation?.status) {
        case ReservationStatus.CONFIRMED:
            statusIcon = <CheckIcon sx={eventIconSx}/>
            break
        case ReservationStatus.ACTIVE:
            statusIcon = <CheckCircleIcon sx={eventIconSx}/>
            break
        case ReservationStatus.CANCELLED:
            statusIcon = <DisabledByDefaultIcon sx={eventIconSx}/>
            break
        case ReservationStatus.COMPLETED:
            statusIcon = <LogoutIcon sx={eventIconSx}/>
            break
        case ReservationStatus.NO_SHOW:
            statusIcon = <WarningIcon sx={eventIconSx}/>
            break
        case ReservationStatus.PENDING:
            statusIcon = <HourglassBottomIcon sx={eventIconSx}/>
            break
        default:
            statusIcon = null
    }

    let slotBorderColor: string
    try {
        slotBorderColor = darken(backgroundColor, 0.15)
    } catch {
        slotBorderColor = CommonThemeColors.sys.palette.blueGray["600"]
    }

    const contextIcon = reservation?.fromBookingEngine
        ? <BookOnlineIcon sx={secondaryIconSx}/>
        : room
            ? <HotelIcon sx={secondaryIconSx}/>
            : null
    const serialIndicator = isSerial ? (
        <Box
            component="span"
            title={t('Slot type SERIAL')}
            role="img"
            aria-label={t('Slot type SERIAL')}
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                color: textColor
            }}
        >
            <CachedIcon sx={secondaryIconSx}/>
        </Box>
    ) : null

    const calendarGuestRow = (
        <Box sx={calendarTopRowSx}>
            <Box
                title={statusIcon ? statusLabel : undefined}
                role={statusIcon ? 'img' : undefined}
                aria-label={statusIcon ? statusLabel : undefined}
                sx={calendarIconRailSx}
            >
                {statusIcon}
            </Box>
            <Typography
                color={textColor}
                title={guestTitle}
                whiteSpace="nowrap"
                sx={{
                    ...calendarTextSx,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    fontWeight: 500,
                    [NARROW_EVENT_CONTAINER_QUERY]: {
                        paddingLeft: '4px'
                    }
                }}
            >
                {resName}
            </Typography>
            <Typography
                color={textColor}
                title={guestTitle}
                whiteSpace="nowrap"
                sx={{
                    ...calendarTextSx,
                    textAlign: 'right',
                }}
            >
                {totalGuestCount}
                {hasCompleteAgeBreakdown && (
                    <Box
                        component="span"
                        sx={{
                            display: 'inline',
                            [NARROW_EVENT_CONTAINER_QUERY]: {
                                display: 'none'
                            }
                        }}
                    >
                        {guestBreakdownLabel}
                    </Box>
                )}
            </Typography>
        </Box>
    )

    const eventSurfaceSx = {
        borderRadius: '4px',
        padding: isCalendarEvent ? '2px 4px' : '2px 8px',
        paddingTop: '2px',
        backgroundColor,
        gap: isCalendarEvent ? 0 : '5px',
        justifyContent: isCalendarEvent ? 'flex-start' : 'space-between',
        alignItems: 'flex-start',
        width: '100%',
        height: '100%',
        position: 'relative',
        containerType: isCalendarEvent ? 'inline-size' : undefined,
        boxSizing: 'border-box',
        overflow: 'hidden',
        border: `0.75px solid ${slotBorderColor}`,
        borderTop: `${calendarView === 'timeline' ? 3 : 5}px solid ${statusAccentColor}`,
        zIndex: highlightSearchResult ? 1 : undefined,
        boxShadow: highlightSearchResult
            ? [
                `0 0 0 3px ${theme.palette.error.main}`,
                `0 0 18px 7px ${alpha(theme.palette.error.main, 0.7)}`,
            ].join(', ')
            : 'none'
    } as const

    if (calendarView === 'timeline') {
        const hasLinkedResources = numOfAttachedResources > 1

        return (
            <Stack
                onClick={onReservationClick}
                sx={{
                    ...eventSurfaceSx,
                    containerType: 'size'
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '1px',
                        width: '100%',
                        height: '100%',
                        minWidth: 0,
                        minHeight: 0,
                        overflow: 'hidden'
                    }}
                >
                    {calendarGuestRow}

                    {(isSerial || contextText || hasLinkedResources) && (
                        <Box
                            sx={{
                                ...calendarRowSx,
                                gridTemplateColumns: isSerial
                                    ? '16px 16px minmax(0, 1fr) auto'
                                    : '16px minmax(0, 1fr) auto'
                            }}
                        >
                            {serialIndicator}
                            <Box
                                title={contextIcon ? contextText : undefined}
                                sx={calendarIconRailSx}
                            >
                                {contextIcon}
                            </Box>
                            <Typography
                                color={textColor}
                                title={contextText || undefined}
                                whiteSpace="nowrap"
                                sx={{
                                    ...calendarTextSx,
                                    gridColumn: hasLinkedResources
                                        ? (isSerial ? '3' : '2')
                                        : (isSerial ? '3 / -1' : '2 / -1'),
                                    minWidth: 0,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                }}
                            >
                                {contextText}
                            </Typography>

                            {hasLinkedResources && (
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    sx={{
                                        gridColumn: isSerial ? '4' : '3',
                                        minWidth: 0,
                                        columnGap: '2px',
                                        flexShrink: 0,
                                        opacity: 0.65
                                    }}
                                >
                                    <InsertLinkIcon sx={secondaryIconSx}/>
                                    <Typography
                                        color={textColor}
                                        whiteSpace="nowrap"
                                        sx={{...calendarTextSx, fontWeight: 400}}
                                    >
                                        {numOfAttachedResources}
                                    </Typography>
                                </Stack>
                            )}
                        </Box>
                    )}

                    {note && (
                        <Box
                            sx={{
                                display: 'none',
                                flexDirection: 'column',
                                flex: 1,
                                minHeight: 0,
                                overflow: 'hidden',
                                [TIMELINE_NOTE_CONTAINER_QUERY]: {
                                    display: 'flex'
                                }
                            }}
                        >
                            <Divider
                                sx={{
                                    width: '100%',
                                    my: '3px',
                                    flexShrink: 0,
                                    borderColor: textColor,
                                    opacity: 0.25
                                }}
                            />
                            <Stack direction="row" alignItems="flex-start" gap={0.5} sx={{minWidth: 0}}>
                                <NoteGroupIcon
                                    note={noteEntry}
                                    index={noteGroupIndex}
                                    fontSize={12}
                                    color={textColor}
                                />
                                <Typography
                                    color={textColor}
                                    title={note}
                                    whiteSpace="nowrap"
                                    sx={{
                                        minWidth: 0,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        fontSize: {
                                            xs: theme.typography.caption.fontSize,
                                            md: theme.typography.body2.fontSize
                                        },
                                        lineHeight: 1.25
                                    }}
                                >
                                    {note}
                                </Typography>
                            </Stack>
                        </Box>
                    )}
                </Box>
            </Stack>
        )
    }

    return (
        <Stack
            onClick={onReservationClick}
            sx={eventSurfaceSx}
        >
            {isCalendarEvent ? (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '1px',
                        width: '100%',
                        minWidth: 0,
                        minHeight: 0,
                        height: '100%',
                        overflow: 'hidden'
                    }}
                >
                    {calendarGuestRow}

                    {(isSerial || !!contextText) && (
                        <Box
                            sx={{
                                ...calendarRowSx,
                                gridTemplateColumns: isSerial
                                    ? '16px 16px minmax(0, 1fr)'
                                    : '16px minmax(0, 1fr)'
                            }}
                        >
                            {serialIndicator}
                            <Box
                                title={contextIcon ? contextText : undefined}
                                sx={calendarIconRailSx}
                            >
                                {contextIcon}
                            </Box>
                            <Typography
                                color={textColor}
                                title={contextText}
                                whiteSpace="nowrap"
                                sx={{
                                    ...calendarTextSx,
                                    gridColumn: isSerial ? '3 / -1' : '2 / -1',
                                    minWidth: 0,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                }}
                            >
                                {contextText}
                            </Typography>
                        </Box>
                    )}

                    {numOfAttachedResources > 1 && (
                        <Box sx={calendarRowSx}>
                            <Box sx={calendarIconRailSx}>
                                <InsertLinkIcon sx={{...secondaryIconSx, opacity: 0.65}}/>
                            </Box>
                            <Typography
                                color={textColor}
                                whiteSpace="nowrap"
                                sx={{
                                    ...calendarTextSx,
                                    gridColumn: '2 / -1',
                                    minWidth: 0,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    fontWeight: 400,
                                    opacity: 0.65
                                }}
                            >
                                {numOfAttachedResources}
                                <Box
                                    component="span"
                                    sx={{
                                        [NARROW_EVENT_CONTAINER_QUERY]: {
                                            display: 'none'
                                        }
                                    }}
                                >
                                    {' '}{t('Linked resources')}
                                </Box>
                            </Typography>
                        </Box>
                    )}

                    {note && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                flex: 1,
                                minHeight: 0,
                                overflow: 'hidden'
                            }}
                        >
                            <Divider
                                sx={{
                                    width: '100%',
                                    my: '3px',
                                    flexShrink: 0,
                                    borderColor: textColor,
                                    opacity: 0.25
                                }}
                            />
                            <Box
                                sx={{
                                    flex: 1,
                                    minHeight: 0,
                                    overflow: 'hidden'
                                }}
                            >
                                <Stack direction="row" alignItems="flex-start" gap={0.5} sx={{minWidth: 0}}>
                                    <NoteGroupIcon
                                        note={noteEntry}
                                        index={noteGroupIndex}
                                        fontSize={12}
                                        color={textColor}
                                    />
                                    <Typography
                                        color={textColor}
                                        title={note}
                                        sx={{
                                            minWidth: 0,
                                            whiteSpace: 'normal',
                                            fontSize: {
                                                xs: theme.typography.caption.fontSize,
                                                md: theme.typography.body2.fontSize
                                            },
                                            lineHeight: 1.25
                                        }}
                                    >
                                        {note}
                                    </Typography>
                                </Stack>
                            </Box>
                        </Box>
                    )}
                </Box>
            ) : (
                <>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'auto minmax(0, 1fr) auto',
                            columnGap: '5px',
                            alignItems: 'center',
                            width: '100%'
                        }}
                    >
                        <Box
                            title={statusIcon ? statusLabel : undefined}
                            role={statusIcon ? 'img' : undefined}
                            aria-label={statusIcon ? statusLabel : undefined}
                            sx={{display: 'flex', flexShrink: 0}}
                        >
                            {statusIcon}
                        </Box>
                        <Typography
                            color={textColor}
                            title={guestTitle}
                            fontSize={fontSizeTop}
                            whiteSpace="nowrap"
                            sx={{minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis'}}
                        >
                            {resName}
                        </Typography>
                        <Typography
                            color={textColor}
                            title={guestTitle}
                            fontSize={fontSizeTop}
                            whiteSpace="nowrap"
                            sx={{flexShrink: 0, textAlign: 'right'}}
                        >
                            {guestCountLabel}
                        </Typography>
                    </Box>

                    <Stack
                        direction="row"
                        sx={{
                            gap: '5px',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '100%'
                        }}
                    >
                        <Box sx={{display: 'flex', alignItems: 'center', gap: '2px', minWidth: 0}}>
                            {serialIndicator}
                            <Box
                                title={contextIcon ? contextText : undefined}
                                sx={{display: 'flex', flexShrink: 0}}
                            >
                                {contextIcon}
                            </Box>
                            <Typography
                                color={textColor}
                                title={contextText || undefined}
                                fontSize={fontSizeBottom}
                                whiteSpace="nowrap"
                                sx={{minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis'}}
                            >
                                {contextText}
                            </Typography>
                        </Box>
                        <Box sx={{display: 'flex', alignItems: 'center', flexShrink: 0}}>
                            <InsertLinkIcon sx={{color: textColor}}/>
                            <Typography color={textColor} fontSize={fontSizeTop} whiteSpace="nowrap">
                                {numOfAttachedResources}
                            </Typography>
                        </Box>
                    </Stack>
                </>
            )}
        </Stack>
    )
}

export default ReservationEventPreview
